import apiRequest from '../../../utils/apiClient';
import { brandLogoUrl, exportStyleUrl } from '../../constants';
import {
  CAPTION_BOUNCES,
  CAPTION_FONTS,
  CAPTION_HIGHLIGHTS,
  CAPTION_LOOKS,
  CAPTION_MODES,
  CAPTION_PALETTES,
  CAPTION_RES,
  CAPTION_REVEALS,
  CAPTION_SIZES,
  INTROS,
  SHAPES,
  TRANSITIONS,
} from './exportText';
import { presetByKey } from './captionPresets';

// My Style — the account's saved export look (transcript/export_style.py). The export window
// opens with it and saves it; Ready-to-post sends it; the server applies it to Claude and
// ChatGPT exports. Stored with make-video's own field names, so the window's choices are
// translated here, in one place, both ways.

const has = (list, key) => list.some((x) => x.key === key);

/** The export window's choices → a style (make-video field names). */
export function windowToStyle(w) {
  const shape = SHAPES.find((s) => s.key === w.shape) || SHAPES[0];
  const size = CAPTION_SIZES.find((s) => s.key === w.sizeKey) || CAPTION_SIZES.find((s) => s.key === 'M');
  return {
    orientation: shape.orientation,
    burn_captions: !!w.captions,
    caption_mode: w.mode,
    words_per_group: w.wordsPerGroup,
    caption_style: w.paletteKey,
    caption_size: size.size,
    caption_font_key: w.fontKey,
    caption_look: w.lookKey,
    caption_upper: !!w.upper,
    caption_highlight: w.highlightKey,
    caption_reveal: w.revealKey,
    caption_bounce: w.bounceKey,
    caption_preset: w.presetKey || 'none',
    caption_res: w.resKey,
    intro: w.introKey,
    transition: w.transitionKey,
    ...(w.logo && w.logo.key ? { logo: logoForExport(w.logo) } : {}),
  };
}

/** The logo as the server keeps it — without the preview-only image link. */
export const logoForExport = (logo) => (logo && logo.key
  ? {
    key: logo.key, x: logo.x, y: logo.y, size: logo.size, opacity: logo.opacity, backdrop: logo.backdrop || 'none',
    ...(logo.spot ? { spot: logo.spot } : {}),
    ...(logo.on ? { on: logo.on } : {}),
  }
  : null);

/** Where a new logo starts: top-right corner, small, nearly solid (brand_logo.DEFAULTS). */
export const LOGO_DEFAULTS = { x: 0.88, y: 0.1, size: 0.16, opacity: 0.9, spot: 'tr', on: 'picture', backdrop: 'none' };

/** A saved style → the export window's choices. Only what the window can show comes back;
 *  anything missing or unknown is left out, so that choice keeps its usual default. */
export function styleToWindow(style) {
  if (!style || typeof style !== 'object') return {};
  const out = {};
  const shape = SHAPES.find((s) => s.orientation === style.orientation);
  if (shape) out.shape = shape.key;
  if (typeof style.burn_captions === 'boolean') out.captions = style.burn_captions;
  if (has(CAPTION_MODES, style.caption_mode)) out.mode = style.caption_mode;
  if (Number.isFinite(style.words_per_group)) out.wordsPerGroup = style.words_per_group;
  if (has(CAPTION_PALETTES, style.caption_style)) out.paletteKey = style.caption_style;
  if (Number.isFinite(style.caption_size)) {
    // The nearest of the window's sizes (a style saved elsewhere may hold any px value).
    out.sizeKey = CAPTION_SIZES.reduce((best, s) => (
      Math.abs(s.size - style.caption_size) < Math.abs(best.size - style.caption_size) ? s : best
    )).key;
  }
  if (has(CAPTION_FONTS, style.caption_font_key)) out.fontKey = style.caption_font_key;
  if (has(CAPTION_LOOKS, style.caption_look)) out.lookKey = style.caption_look;
  if (typeof style.caption_upper === 'boolean') out.upper = style.caption_upper;
  if (has(CAPTION_HIGHLIGHTS, style.caption_highlight)) out.highlightKey = style.caption_highlight;
  if (has(CAPTION_REVEALS, style.caption_reveal)) out.revealKey = style.caption_reveal;
  if (has(CAPTION_BOUNCES, style.caption_bounce)) out.bounceKey = style.caption_bounce;
  if (style.caption_preset === 'none') out.presetKey = '';
  else if (presetByKey(style.caption_preset)) out.presetKey = style.caption_preset;
  if (has(CAPTION_RES, style.caption_res)) out.resKey = style.caption_res;
  if (has(INTROS, style.intro)) out.introKey = style.intro;
  if (has(TRANSITIONS, style.transition)) out.transitionKey = style.transition;
  if (style.logo && style.logo.key) out.logo = { ...LOGO_DEFAULTS, ...style.logo };
  return out;
}

/** The caption fields of a style as make-video form fields (what Ready-to-post adds to each
 *  export). The palette and the shape are left out: Ready-to-post has its own pickers. */
export function captionFormFields(style) {
  if (!style) return {};
  const fields = {};
  const keys = ['caption_mode', 'words_per_group', 'caption_size', 'caption_font_key', 'caption_look',
    'caption_highlight', 'caption_reveal', 'caption_bounce', 'caption_preset', 'caption_res', 'intro'];
  keys.forEach((key) => {
    if (style[key] !== undefined && style[key] !== null && style[key] !== '') fields[key] = String(style[key]);
  });
  if (typeof style.caption_upper === 'boolean') fields.caption_upper = style.caption_upper ? 'true' : 'false';
  if (style.logo && style.logo.key) fields.logo = JSON.stringify(logoForExport(style.logo));
  // 'none' is the window's word for off; make-video's is absent.
  ['caption_bounce', 'caption_preset', 'intro'].forEach((key) => { if (fields[key] === 'none') delete fields[key]; });
  return fields;
}

/** True where a saved style belongs to the person looking: not on someone else's share link. */
export const canUseMyStyle = () => typeof window === 'undefined' || !window.location.pathname.startsWith('/s/');

/** The saved style, or null (none saved, signed out, or the server unreachable). */
export async function loadMyStyle() {
  if (!canUseMyStyle()) return null;
  try {
    const response = await apiRequest(exportStyleUrl, { method: 'GET' });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data || !data.style) return null;
    // The logo's image link rides along for the preview (the server drops it on save).
    if (data.style.logo && data.logo_url) return { ...data.style, logo: { ...data.style.logo, url: data.logo_url } };
    return data.style;
  } catch (e) {
    return null;
  }
}

/** Upload a logo image; resolves to {key, url, width, height}, rejects with a message. */
export async function uploadLogo(file) {
  const body = new FormData();
  body.append('file', file);
  const response = await apiRequest(brandLogoUrl, { method: 'POST', body });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'upload failed');
  return data;
}

/** Save a style; resolves to what the server kept, rejects when it couldn't be saved. */
export async function saveMyStyle(style) {
  const response = await apiRequest(exportStyleUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ style }),
  });
  if (!response.ok) throw new Error(`save failed (${response.status})`);
  const data = await response.json();
  return data.style;
}
